import React, { useEffect, useState } from "react";
import { Button, Empty, Skeleton, Badge, Dropdown } from "antd";
import { ReloadOutlined, ArrowRightOutlined, SwapOutlined, FileTextOutlined, BellOutlined, CreditCardOutlined, RiseOutlined, BarChartOutlined, PhoneOutlined, WifiOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDashboard } from "../../../store/context/DashboardContext";
import { useTranslation } from "react-i18next";
import { useFeatures } from "../../../store/context/feature-context";
import { featuresEnum } from "../../../shared/constants/constants";
import SelectAccoutModal from "../../Payment Methods/Modals/SelectAccoutModal";
import DashboardCarousel from "../DashboardCarousel";
import DepositCard from "../../Policies/DepositCard";
import PolicyCard from "../../Policies/PolicyCard";
import { useDashboardLayout } from "../../../store/context/dashboard-layout-context";
import { formatCurrencyWithoutCode, formatDate } from "../../../utils/helper/format";
import { getGreeting } from "../../../utils/helper/getGreeting";
import { darkenHex, hexToRgba } from "../../../utils/helper/colorUtils";
import { useTenant } from "../../../store/context/tenant-context";

/**
 * Design 1 � Modern layout.
 * Forest-green balance card (single tone, no garish multi-colour gradient),
 * clean stat row with real icon colours, tab-filtered accounts.
 */
const ModernGradientLayout = ({
  savedSessionData,
  notificationMenu,
  notificationList,
  onViewProducts,
  layoutSwitcher,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { findFeature } = useFeatures();
  const {
    selectedFilter,
    setSelectedFilter,
    policies,
    fetchPolicies,
    pendingQuotes,
    loading,
    depositPolicies,
    bankingProfile,
  } = useDashboard();

  const requiresAccount = bankingProfile?.hasCoreBankingAccounts === false;

  const { balanceVisible, toggleBalanceVisible } = useDashboardLayout();
  const tenant = useTenant();
  const primary = tenant?.theme?.colorPrimary || "#0b3d2e";
  const darkPrimary  = darkenHex(primary, 0.78);   // subtle gradient start
  const medPrimary   = darkenHex(primary, 0.82);   // used for hover states
  const lightPrimary = hexToRgba(primary, 0.1);    // light tint  — icon bg
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);

  // Keep selected account in sync when depositPolicies first loads
  useEffect(() => {
    if (depositPolicies.length > 0 && !selectedAccountNumber) {
      setSelectedAccountNumber(depositPolicies[0].accountNumber);
    }
  }, [depositPolicies, selectedAccountNumber]);

  const selectedAccount =
    depositPolicies.find((a) => a.accountNumber === selectedAccountNumber) ||
    depositPolicies[0];

  const selectedBalance =
    selectedAccount?.availableBalance || selectedAccount?.accountBalance || 0;

  // Auto-select loans if deposits filter is active but user has no deposits
  useEffect(() => {
    if (!loading && selectedFilter === "deposits" && policies.length > 0) {
      const hasDeposits = policies.some((p) => p.type === "deposits");
      const hasLoans = policies.some((p) => p.type === "loans");
      if (!hasDeposits && hasLoans) {
        setSelectedFilter("loans");
      }
    }
  }, [loading, policies, selectedFilter, setSelectedFilter]);

  const categories = [
    { key: "deposits", label: t("Deposits") },
    { key: "loans", label: t("Loans") },
    { key: "investments", label: t("Investments") },
  ].filter((c) => {
    if (c.key === "loans") return findFeature(featuresEnum.CUSTOMER_LOAN);
    return true;
  });

  const filteredPolicies = policies
    .slice()
    .sort((a, b) => a.type.localeCompare(b.type))
    .filter((p) => p.type.includes(selectedFilter));

  const stats = [
    {
      label: t("Active Loans"),
      value: policies.filter((p) => p.type === "loans").length,
      suffix: t("applications"),
      IconCmp: CreditCardOutlined,
      accent: primary,
      bg: hexToRgba(primary, 0.1),
    },
    {
      label: t("Savings Rate"),
      value: "10% p.a.",
      suffix: "",
      IconCmp: RiseOutlined,
      accent: "#e5791b",
      bg: "#fff4ea",
    },
    {
      label: t("Investments"),
      value: policies.filter((p) => p.type === "investments").length,
      suffix: t("active"),
      IconCmp: BarChartOutlined,
      accent: "#1a5fac",
      bg: "#e8f0fc",
    },
  ];

  return (
    <div style={{ background: "#f4f6f4", minHeight: "100vh" }}>

      <div style={{ padding: "24px 28px", overflow: "hidden" }}>
        <SelectAccoutModal
          isModalVisible={isSwitchModalOpen}
          handleCancel={() => setIsSwitchModalOpen(false)}
          handleOk={(accountNumber) => {
            setSelectedAccountNumber(accountNumber);
            setIsSwitchModalOpen(false);
          }}
        />
        {/* -- Balance card — modern gradient with decorative circles -- */}
        <div
          style={{
            background: `linear-gradient(135deg, ${darkPrimary} 0%, ${primary} 100%)`,
            borderRadius: 20,
            padding: "28px 32px",
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
            boxShadow: "0 8px 32px rgba(13,61,43,0.3)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* decorative circles from the HTML design */}
          <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, background: "rgba(255,255,255,0.06)", borderRadius: "50%", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -60, right: 100, width: 160, height: 160, background: "rgba(255,255,255,0.04)", borderRadius: "50%", pointerEvents: "none" }} />
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", textTransform: "uppercase", letterSpacing: "1.2px", margin: 0 }}>
                {t("Account Balance")}
              </p>
              <button
                onClick={() => toggleBalanceVisible()}
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 6, padding: "2px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: "rgba(255,255,255,0.8)", fontSize: 11 }}
              >
                {balanceVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                {balanceVisible ? t("Hide") : t("Show")}
              </button>
            </div>
            <p style={{ fontSize: 38, fontWeight: 800, color: "#fff", margin: "0 0 10px", letterSpacing: "-1px", transition: "all 0.2s" }}>
              {"₦"}{balanceVisible ? formatCurrencyWithoutCode(selectedBalance) : "***.** "}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>
                {selectedAccount?.accountType || t("Savings")}
              </span>
              {selectedAccount?.accountNumber && (
                <span style={{ fontSize: 12, color: "#fff", background: "rgba(255,255,255,0.18)", padding: "3px 10px", borderRadius: 20, fontFamily: "monospace", letterSpacing: "1px", fontWeight: 600, border: "1px solid rgba(255,255,255,0.25)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: selectedAccount.active ? "#4ade80" : "#f87171", flexShrink: 0, boxShadow: selectedAccount.active ? "0 0 6px #4ade80" : "0 0 6px #f87171" }} />
                  {selectedAccount.accountNumber}
                </span>
              )}
            </div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: "8px 0 0" }}>
              {t("Last updated")}: {t("just now")} 🔄
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, zIndex: 1 }}>
            <Button
              icon={<SwapOutlined />}
              onClick={() => navigate("/transfer")}
              style={{ background: "#fff", border: "none", color: primary, borderRadius: 10, height: 40, fontWeight: 700, padding: "0 22px", minWidth: 160 }}
            >
              + {t("Transfer")}
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => navigate("/account-statement")}
              style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 10, height: 40, fontWeight: 600, padding: "0 22px", minWidth: 160 }}
            >
              {t("View Statement")}
            </Button>
            {depositPolicies.length > 1 && (
              <Button
                onClick={() => setIsSwitchModalOpen(true)}
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 10, height: 40, fontWeight: 600, padding: "0 22px", minWidth: 160 }}
              >
                {t("Switch Account")}
              </Button>
            )}
          </div>
        </div>

        {/* -- Stat row --------------------------------------- */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 20 }}>
          {stats.map((s) => (
            <div
              key={s.label}
              style={{
                background: "#fff", borderRadius: 16, padding: "18px 20px",
                border: "1px solid #e8ede8", display: "flex", alignItems: "center", gap: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  width: 48, height: 48, borderRadius: 12, background: s.bg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <s.IconCmp style={{ fontSize: 22, color: s.accent }} />
              </div>
              <div>
                <p style={{ fontSize: 12, color: "#888", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: ".6px" }}>{s.label}</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>{s.value} <span style={{ fontSize: 11, color: "#27ae60", fontWeight: 500 }}>{s.suffix}</span></p>
              </div>
            </div>
          ))}
        </div>

        {/* -- Main content ----------------------------------- */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0, overflow: "hidden" }}>

            {!!pendingQuotes?.length && (
              <div style={{ background: "#fff", borderRadius: 16, padding: "20px 22px", border: "1px solid #e8ede8", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <p style={{ fontWeight: 700, fontSize: 14, color: "#111", margin: 0 }}>{t("My Pending Applications")}</p>
                  <button onClick={() => navigate("/loans")} style={{ fontSize: 12, color: primary, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                    {t("View all")} →
                  </button>
                </div>
                {pendingQuotes.slice(0, 3).map((quote, i) => (
                  <div key={i} onClick={() => navigate(`/loans/${quote.id}`)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: i < Math.min(pendingQuotes.length, 3) - 1 ? "1px solid #f5f5f5" : "none", cursor: "pointer" }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: "#f0f2f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FileTextOutlined style={{ color: primary, fontSize: 16 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{quote.accountType || t("Loan")}</p>
                      <p style={{ fontSize: 11, color: "#999", margin: 0 }}>Loan · {formatDate(quote.dateOpened)}</p>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>₦{parseFloat(quote.loanAmount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <span style={{ fontSize: 10, padding: "2px 8px", background: "#fff3ea", color: "#e5791b", borderRadius: 20, fontWeight: 600, display: "inline-block", marginTop: 2 }}>Pending</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* My Accounts */}
            <div style={{ background: "#fff", borderRadius: 18, padding: "20px 22px", border: "1px solid #e8ede8" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <p style={{ fontWeight: 700, fontSize: 14, color: "#111", margin: 0 }}>{t("My Accounts")}</p>
                <Button icon={<ReloadOutlined />} size="small" type="text" onClick={fetchPolicies} />
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {categories.map((c) => (
                  <button
                    key={c.key}
                    disabled={requiresAccount && c.key !== "loans"}
                    onClick={() => setSelectedFilter(c.key)}
                    style={{
                      padding: "6px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", border: "none", transition: "all .15s",
                      background: selectedFilter === c.key ? primary : "#f0f2f0",
                      color: selectedFilter === c.key ? "#fff" : "#555",
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {loading ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : filteredPolicies.length > 0 ? (
                <div style={{ overflow: "hidden" }}>
                  <DashboardCarousel
                    slides={filteredPolicies.map((policy) =>
                      policy.type === "deposits" || policy.type === "loans" ? (
                        <DepositCard key={policy.policyNumber} policy={policy} balanceVisible={balanceVisible} />
                      ) : (
                        <PolicyCard key={policy.policyNumber} policy={policy} />
                      ),
                    )}
                  />
                </div>
              ) : (
                <Empty description={`${t("No")} ${selectedFilter} ${t("available")}`} />
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ background: "#fff", borderRadius: 16, padding: "20px", border: "1px solid #e8ede8", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <p style={{ fontWeight: 700, fontSize: 14, color: "#111", margin: "0 0 16px" }}>{t("Quick Actions")}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { Icon: PhoneOutlined, label: t("Airtime"), path: "/airtime", bg: "#e8f0fc", color: "#1a5fac" },
                { Icon: WifiOutlined, label: t("Data"), path: "/data", bg: "#e8f5ee", color: "#0b7a45" },
                { Icon: CreditCardOutlined, label: t("Bills"), path: "/bills-payment", bg: "#fff3ea", color: "#e5791b" },
                { Icon: SwapOutlined, label: t("Transfer"), path: "/transfer", bg: "#fce8f3", color: "#7c3aed" },
              ].map(({ Icon, label, path, bg, color }) => (
                <button
                  key={label}
                  onClick={() => navigate(path)}
                  style={{
                    border: "1.5px solid #f0f0f0", borderRadius: 14, padding: "16px 12px",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                    cursor: "pointer", background: "#fff", transition: "all .2s",
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = medPrimary; e.currentTarget.style.background = lightPrimary; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = "#f0f0f0"; e.currentTarget.style.background = "#fff"; }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon style={{ fontSize: 20, color }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernGradientLayout;
